import { t } from 'i18next';
import Menu, { MenuItem } from 'rc-menu';
import { ReactNode, SyntheticEvent, useRef, useState } from 'react';
import { useIsTouchDevice } from '../../../../../../shared/frontend/components/grid/hooks';
import { IconButton } from '../../../../../../shared/frontend/components/presentational/buttons/IconButton';
import {
  ComplexTooltip,
  TooltipPlacement
} from '../../../../../../shared/frontend/components/presentational/tooltip';
import { handleRCMenuOnClick } from '../../../../../../shared/frontend/libs/history';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';

type TTalentLoadoutActionsMenuProps = {
  loadoutText?: string;
  compareUrl?: string;
  talentCalculatorUrl?: string;
  characterUrl?: string;
  findVideosUrl?: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  placement?: TooltipPlacement;
  // an element to hang the menu off instead of the kebab, for rows that already draw their own affordance
  children?: ReactNode;
};

type TMenuAction = {
  key: EMenuActionKey;
  icon: ReactNode;
  label: string;
  href?: string;
  onSelect?: () => void;
};

const enum EMenuActionKey {
  CopyTalents = 'copyTalents',
  CompareTalents = 'compareTalents',
  ViewCharacter = 'viewCharacter',
  ViewTalents = 'viewTalents',
  FindVideosFromSpec = 'findVideosFromSpec',
  ToggleExpand = 'toggleExpand'
}

function MenuLabel({
  icon,
  label
}: {
  icon: ReactNode;
  label: string;
}) {
  return (
    <span className="rio-talent-row-actions__menu-item">
      <span className="rio-talent-row-actions__icon">{icon}</span>
      <span>{label}</span>
    </span>
  );
}

export function TalentLoadoutActionsMenu({
  loadoutText,
  compareUrl,
  talentCalculatorUrl,
  characterUrl,
  findVideosUrl,
  isExpanded = false,
  onToggleExpand,
  placement = 'left',
  children
}: TTalentLoadoutActionsMenuProps) {
  const rioIconUrl = asset('/images/brand/Mark_White.svg');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showCopyCheck, setShowCopyCheck] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const isTouchDevice = useIsTouchDevice();

  const stopPropagation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  const actions: TMenuAction[] = [
    ...(loadoutText
      ? [
          {
            key: EMenuActionKey.CopyTalents,
            icon: <i className="fa fa-fw fa-copy" />,
            label: t('mplus-runs:row-actions.copy-talents'),
            onSelect: () => {
              window.rioCopyStringToClipboard(loadoutText);
              clearTimeout(copyTimerRef.current);
              setShowCopyCheck(true);
              copyTimerRef.current = setTimeout(() => setShowCopyCheck(false), 1500);
            }
          },
          ...(compareUrl
            ? [
                {
                  key: EMenuActionKey.CompareTalents,
                  icon: <i className="fa fa-fw fa-code-compare" />,
                  label: t('mplus-runs:row-actions.compare-talents'),
                  href: compareUrl
                }
              ]
            : []),
          {
            key: EMenuActionKey.ViewTalents,
            icon: <i className="fa fa-fw fa-calculator" />,
            label: t('mplus-runs:row-actions.view-talents-wowhead'),
            href: talentCalculatorUrl
          }
        ]
      : []),
    ...(findVideosUrl
      ? [
          {
            key: EMenuActionKey.FindVideosFromSpec,
            icon: <i className="fa fa-fw fa-video-camera" />,
            label: t('mplus-runs:row-actions.find-videos-from-spec'),
            href: findVideosUrl
          }
        ]
      : []),
    ...(onToggleExpand
      ? [
          {
            key: EMenuActionKey.ToggleExpand,
            icon: <i className="fa fa-fw fa-shield-halved" />,
            label: t(isExpanded ? 'mplus-runs:row-actions.hide-loadout' : 'mplus-runs:row-actions.show-loadout'),
            onSelect: onToggleExpand
          }
        ]
      : []),
    ...(characterUrl
      ? [
          {
            key: EMenuActionKey.ViewCharacter,
            icon: <img src={rioIconUrl} alt="" className="rio-talent-row-actions__icon-image" />,
            label: t('mplus-runs:row-actions.view-character-profile'),
            href: characterUrl
          }
        ]
      : [])
  ];

  const handleMenuClick = ({ key, domEvent }: { key: string; domEvent: any }) => {
    domEvent.preventDefault();
    domEvent.stopPropagation();

    const action = actions.find((menuAction) => menuAction.key === key);

    if (action?.href) {
      handleRCMenuOnClick({ key: action.href, domEvent });
    } else {
      action?.onSelect?.();
    }

    if (key !== EMenuActionKey.CopyTalents) {
      setIsMenuOpen(false);
    }
  };

  return (
    <ComplexTooltip
      placement={placement}
      renderJustChildren
      clickableTooltip
      showArrow={false}
      trigger={isTouchDevice ? 'click' : 'hover'}
      destroyTooltipOnHide
      noPaddingInner
      mouseLeaveDelay={isTouchDevice ? 0 : 0.1}
      visible={isMenuOpen}
      onVisibleChange={setIsMenuOpen}
      onClick={stopPropagation}
      tooltip={
        <div onClick={stopPropagation}>
          <Menu
            onClick={handleMenuClick}
            selectable={false}
            className="rio-floating-menu rio-menu-no-vertical-padding rio-talent-row-actions__menu"
          >
            {actions.map((action) => (
              <MenuItem key={action.key}>
                <MenuLabel icon={action.icon} label={action.label} />
              </MenuItem>
            ))}
          </Menu>
        </div>
      }
    >
      {children ?? (
        <IconButton
          size="small"
          variant="text"
          icon={
            <span
              style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <i
                className="fa fa-ellipsis-vertical fa-lg"
                style={{ transition: 'opacity 0.2s ease', opacity: showCopyCheck ? 0 : 1 }}
              />
              <i
                className="fa fa-circle-check fa-lg"
                style={{
                  position: 'absolute',
                  transition: 'opacity 0.2s ease',
                  opacity: showCopyCheck ? 1 : 0,
                  color: '#fff'
                }}
              />
            </span>
          }
          aria-label={t('mplus-runs:row-actions.more-actions')}
          title={t('mplus-runs:row-actions.more-actions')}
        />
      )}
    </ComplexTooltip>
  );
}
